import { useMutation } from '@apollo/client';
import { message } from 'antd';
import { useEffect, useState, useMemo } from 'react';
import {
  MUTATION_CREATE_PROPERTY,
  MUTATION_UPDATE_GALLERY,
  MUTATION_COMPLETE_ONBOARDING,
} from '~pages/onboarding/queries';
import { onboardingProcesses } from './context';

const UPDATE_GALLERY_IMAGE_TYPE = 'Image';

const useOnboarding = ({ landlordId, propertyInfo }) => {
  const [process, setProcess] = useState();
  const [propertyTypeSlug, setPropertyTypeSlug] = useState('');
  const [residentTypeSlug, setResidentTypeSlug] = useState('');
  const [propertyName, setPropertyName] = useState();
  const [propertyId, setPropertyId] = useState();
  const [coverPhoto, setCoverPhoto] = useState(null);
  const [businessFormRef, setBusinessFormRef] = useState(null);

  const hasCoverPhoto = useMemo(() => propertyInfo?.image, [propertyInfo?.image]);

  useEffect(() => {
    if (propertyInfo && !propertyInfo?.loading && !process) {
      if (propertyInfo?.id) {
        setProcess(onboardingProcesses.RECALLED_TO);
        setPropertyId(propertyInfo?.id);
        setPropertyTypeSlug(propertyInfo?.propertyType?.slug);
      } else {
        setProcess(onboardingProcesses.PROPERTY_TYPE);
      }
    }
  }, [process, propertyInfo]);

  // step 3
  const [mapAddressData, setMapAddressData] = useState({
    city: null,
    cityId: null,
    country: null,
    countryShortName: null,
    street: null,
    zipcode: null,
    address: null,
    currency: null,
    coordinates: {
      lat: 0,
      lng: 0,
    },
  });

  const [mutationCreateProperty, { loading: createPropertyLoading }] = useMutation(
    MUTATION_CREATE_PROPERTY,
    {
      onCompleted: e => {
        setPropertyId(e?.createProperty?.property?.id);
        setProcess(onboardingProcesses.COVER_PHOTO);
      },
      onError: e => {
        message.error(e.toString());
      },
    },
  );

  const [mutationUpdateGallery, { loading: updateGalleryLoading }] = useMutation(
    MUTATION_UPDATE_GALLERY,
    {
      onCompleted: () => {
        setProcess(onboardingProcesses.BUSINESS_INFO);
      },
      onError: e => {
        message.error(e.toString());
      },
    },
  );

  const [mutationCompleteOnboarding] = useMutation(MUTATION_COMPLETE_ONBOARDING, {
    onCompleted: () => {
      setProcess(onboardingProcesses.SUCCESSFUL);
    },
    onError: e => {
      message.error(e.toString());
    },
  });

  const createPropertyVariables = useMemo(() => {
    return {
      input: {
        address: mapAddressData?.address,
        cityId: mapAddressData?.cityId,
        country: mapAddressData?.countryShortName,
        currency: mapAddressData?.currency,
        landlordId,
        latitude: mapAddressData?.coordinates?.lat,
        longitude: mapAddressData?.coordinates?.lng,
        name: propertyName,
        postalCode: mapAddressData?.zipcode,
        propertyType: propertyTypeSlug,
        residentType: residentTypeSlug,
        shippingCity: mapAddressData?.city,
      },
    };
  }, [
    landlordId,
    mapAddressData?.address,
    mapAddressData?.city,
    mapAddressData?.cityId,
    mapAddressData?.coordinates?.lat,
    mapAddressData?.coordinates?.lng,
    mapAddressData?.zipcode,
    mapAddressData?.countryShortName,
    mapAddressData?.currency,
    propertyName,
    propertyTypeSlug,
    residentTypeSlug,
  ]);

  const updateGalleryVariables = useMemo(
    () => ({
      input: {
        propertyId,
        propertyImages: [
          {
            category: coverPhoto?.category?.toUpperCase(),
            hero: true,
            id: btoa(JSON.stringify({ type: UPDATE_GALLERY_IMAGE_TYPE, id: coverPhoto?.id })),
            position: coverPhoto?.position,
          },
        ],
      },
    }),
    [coverPhoto?.category, coverPhoto?.id, coverPhoto?.position, propertyId],
  );

  const handleClickBack = () => {
    switch (process) {
      case onboardingProcesses.RESIDENT_TYPE:
        setProcess(onboardingProcesses.PROPERTY_TYPE);
        return;
      case onboardingProcesses.PROPERTY_ADDRESS:
        setProcess(onboardingProcesses.RESIDENT_TYPE);
        return;
      case onboardingProcesses.PROPERTY_NAME:
        setProcess(onboardingProcesses.PROPERTY_ADDRESS);
        return;
      case onboardingProcesses.COVER_PHOTO:
        setProcess(onboardingProcesses.PROPERTY_NAME);
        return;
      case onboardingProcesses.BUSINESS_INFO: {
        setProcess(onboardingProcesses.COVER_PHOTO);
        return;
      }
    }
  };

  const handleClickNext = () => {
    switch (process) {
      case onboardingProcesses.PROPERTY_TYPE:
        setProcess(onboardingProcesses.RESIDENT_TYPE);
        return;
      case onboardingProcesses.RESIDENT_TYPE:
        setProcess(onboardingProcesses.PROPERTY_ADDRESS);
        return;
      case onboardingProcesses.PROPERTY_ADDRESS:
        setProcess(onboardingProcesses.PROPERTY_NAME);
        return;
      case onboardingProcesses.PROPERTY_NAME:
        mutationCreateProperty({ variables: createPropertyVariables });
        return;
      case onboardingProcesses.COVER_PHOTO:
        mutationUpdateGallery({ variables: updateGalleryVariables });
        return;
      case onboardingProcesses.BUSINESS_INFO: {
        businessFormRef?.current?.props?.onSubmit();
        return;
      }
      case onboardingProcesses.RECALLED_TO: {
        setProcess(
          hasCoverPhoto ? onboardingProcesses.BUSINESS_INFO : onboardingProcesses.COVER_PHOTO,
        );
        return;
      }
    }
  };

  const onboardingData = useMemo(
    () => ({
      process,
      setProcess,
      propertyTypeSlug,
      residentTypeSlug,
      propertyName,
      setPropertyTypeSlug,
      setResidentTypeSlug,
      setPropertyName,
      mapAddressData,
      setMapAddressData,
      createPropertyLoading,
      coverPhoto,
      setCoverPhoto,
      propertyId,
      landlordId,
      updateGalleryLoading,
      setBusinessFormRef,
      mutationCompleteOnboarding,
    }),
    [
      process,
      propertyTypeSlug,
      residentTypeSlug,
      propertyName,
      mapAddressData,
      createPropertyLoading,
      coverPhoto,
      propertyId,
      landlordId,
      updateGalleryLoading,
      mutationCompleteOnboarding,
    ],
  );

  useEffect(() => {
    window.onbeforeunload = () => {
      if (
        onboardingData?.propertyTypeSlug ||
        onboardingData?.residentTypeSlug ||
        onboardingData?.propertyName
      ) {
        return true;
      }
      return null;
    };

    return () => {
      window.onbeforeunload = null;
    };
  }, [onboardingData]);

  return {
    onboardingData,
    handleClickBack,
    handleClickNext,
  };
};

export default useOnboarding;
